import React, { useState } from 'react';
import {
  Button,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { SelectLocation } from '../../../common';
import FrameInventorySelector from '../../../common/FrameInventorySelector';
import SimpleDatePicker from '../../../ui/simpleDatePicker';

export default function ReceiveStockTab({
  receiveMode,
  setReceiveMode,
  suppliers,
  receiveForm,
  openPoOptions,
  selectedPurchaseOrder,
  selectedLocationName,
  onReceiveFieldChange,
  onReceivePoSelect,
  onReceiveLineChange,
  addDirectReceiveLine,
  removeDirectReceiveLine,
  setReceiveForm,
  buildDirectReceiveLine,
  submitReceive,
  onCancelPurchaseOrder,
  isSubmitting
}) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const getInvoiceReference = (purchaseOrder) => {
    const noteSources = [purchaseOrder?.notes, ...(purchaseOrder?.lines || []).map((line) => line?.notes)].filter(Boolean);

    for (const note of noteSources) {
      const match = String(note).match(/Invoice\s+([^\-\n]+)/i);
      if (match?.[1]) {
        return match[1].trim();
      }
    }

    return null;
  };

  const getPurchaseOrderDetails = (purchaseOrder) => {
    const invoiceReference = getInvoiceReference(purchaseOrder);
    const notes = String(purchaseOrder?.notes || '').trim();

    return {
      invoiceReference,
      notes,
      displayNotes: notes && notes !== '[INVOICE ORDER]' ? notes : '',
    };
  };

  const isInvoiceOriginatedPurchaseOrder = (purchaseOrder) => {
    const purchaseOrderNotes = String(purchaseOrder?.notes || '');
    if (purchaseOrderNotes.includes('[INVOICE ORDER]')) {
      return true;
    }

    return (purchaseOrder?.lines || []).some((line) => String(line?.notes || '').includes('[INVOICE ORDER]'));
  };

  const confirmCancelPurchaseOrder = () => {
    onCancelPurchaseOrder?.(cancelReason.trim());
    setCancelDialogOpen(false);
    setCancelReason('');
  };

  return (
    <>
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Receive Mode"
            value={receiveMode}
            onChange={(e) => {
              const mode = e.target.value;
              setReceiveMode(mode);
              setReceiveForm(prev => ({
                ...prev,
                purchaseOrderId: '',
                supplierId: '',
                locationId: '',
                lines: mode === 'direct' ? [buildDirectReceiveLine()] : []
              }));
            }}
          >
            <MenuItem value="po">Against Purchase Order</MenuItem>
            <MenuItem value="direct">Direct Receive (No PO)</MenuItem>
          </TextField>
        </Grid>

        {receiveMode === 'po' && (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Supplier Filter"
                value={receiveForm.supplierId}
                onChange={(e) => onReceiveFieldChange('supplierId', e.target.value)}
              >
                <MenuItem value="">All Suppliers</MenuItem>
                {suppliers.map(supplier => (
                  <MenuItem key={supplier.id} value={supplier.id}>{supplier.supplierName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Purchase Order"
                value={receiveForm.purchaseOrderId}
                onChange={(e) => onReceivePoSelect(e.target.value)}
              >
                {openPoOptions.map(po => (
                  <MenuItem key={po.id} value={po.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2">PO #{po.id}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {po.supplier?.supplierName || 'Supplier'}
                        </Typography>
                        {isInvoiceOriginatedPurchaseOrder(po) && (
                          <Chip size="small" color="info" label="Invoice Order" />
                        )}
                      </Box>
                      {getPurchaseOrderDetails(po).invoiceReference && (
                        <Typography variant="caption" color="text.secondary">
                          Invoice {getPurchaseOrderDetails(po).invoiceReference}
                        </Typography>
                      )}
                      {getPurchaseOrderDetails(po).displayNotes && (
                        <Typography variant="caption" color="text.secondary">
                          {getPurchaseOrderDetails(po).displayNotes}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                color="error"
                variant="outlined"
                disabled={!selectedPurchaseOrder || isSubmitting}
                onClick={() => setCancelDialogOpen(true)}
              >
                Cancel PO
              </Button>
            </Grid>
            {selectedPurchaseOrder && isInvoiceOriginatedPurchaseOrder(selectedPurchaseOrder) && (
              <Grid item xs={12}>
                <Chip size="small" color="info" label="Invoice-originated order" />
              </Grid>
            )}
            {selectedPurchaseOrder && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>
                    PO #{selectedPurchaseOrder.id} - {selectedPurchaseOrder.supplier?.supplierName || 'Supplier'}
                  </Typography>
                  {getPurchaseOrderDetails(selectedPurchaseOrder).invoiceReference && (
                    <Typography variant="body2" color="text.secondary">
                      Invoice {getPurchaseOrderDetails(selectedPurchaseOrder).invoiceReference}
                    </Typography>
                  )}
                  {getPurchaseOrderDetails(selectedPurchaseOrder).displayNotes && (
                    <Typography variant="body2" color="text.secondary">
                      {getPurchaseOrderDetails(selectedPurchaseOrder).displayNotes}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}
          </>
        )}

        {receiveMode === 'direct' && (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label="Supplier"
                value={receiveForm.supplierId}
                onChange={(e) => onReceiveFieldChange('supplierId', e.target.value)}
              >
                {suppliers.map(supplier => (
                  <MenuItem key={supplier.id} value={supplier.id}>{supplier.supplierName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectLocation
                fullWidth
                location={receiveForm.locationId}
                setLocation={(loc) => onReceiveFieldChange('locationId', loc?.id || '')}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Supplier Invoice #"
            value={receiveForm.supplierInvoiceNumber}
            onChange={(e) => onReceiveFieldChange('supplierInvoiceNumber', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <SimpleDatePicker
            label="Invoice Date"
            value={receiveForm.supplierInvoiceDate}
            onChange={(date) => onReceiveFieldChange('supplierInvoiceDate', date || '')}
            helperTextMode="never"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Consignment Reference"
            value={receiveForm.consignmentReference}
            onChange={(e) => onReceiveFieldChange('consignmentReference', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Consignment Type"
            value={receiveForm.consignmentType}
            onChange={(e) => onReceiveFieldChange('consignmentType', e.target.value)}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="consignment">Consignment</MenuItem>
            <MenuItem value="direct">Direct</MenuItem>
            <MenuItem value="drop_ship">Drop Ship</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2">Receipt Lines</Typography>
        </Grid>

        {!receiveForm.lines.length && (
          <Grid item xs={12}>
            <Chip label={receiveMode === 'po' ? 'Select a purchase order to load pending lines' : 'Add direct receipt lines below'} />
          </Grid>
        )}

        {receiveForm.lines.map((line, index) => {
          return (
            <Grid item xs={12} key={`receive-line-${index}`}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={4}>
                    {receiveMode === 'direct' ? (
                      <FrameInventorySelector
                        label="Inventory Item"
                        value={line.inventoryItem}
                        locationId={receiveForm.locationId}
                        locationName={selectedLocationName}
                        onChange={(selectedItem) => {
                          setReceiveForm(prev => {
                            const lines = [...prev.lines];
                            lines[index] = {
                              ...lines[index],
                              inventoryItemId: selectedItem?.id || '',
                              inventoryItem: selectedItem,
                              catalogProductId: selectedItem?.catalogProductId || null
                            };
                            return { ...prev, lines };
                          });
                        }}
                        helperText={line.inventoryItemId ? '' : 'Type a model number or use search'}
                      />
                    ) : (
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {line.inventoryItem?.modelNumber || line.modelNumber || `Inventory #${line.inventoryItemId}`}
                        </Typography>
                        {(line.notes || line.purchaseOrderLine?.notes || line.purchaseOrderNotes) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                            {line.notes || line.purchaseOrderLine?.notes || line.purchaseOrderNotes}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {receiveMode !== 'direct' && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {line.inventoryItem?.internalUpc || line.inventoryItem?.sku || 'No barcode'}
                        </Typography>
                        {line.purchaseOrderLine?.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                            {line.purchaseOrderLine.notes}
                          </Typography>
                        )}
                      </>
                    )}
                  </Grid>

                  <Grid item xs={4} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Received"
                      value={line.receivedQty}
                      onChange={(e) => onReceiveLineChange(index, 'receivedQty', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={4} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Accepted"
                      value={line.acceptedQty}
                      onChange={(e) => onReceiveLineChange(index, 'acceptedQty', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={4} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Damaged"
                      value={line.damagedQty}
                      onChange={(e) => onReceiveLineChange(index, 'damagedQty', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      inputProps={{ inputMode: 'decimal' }}
                      label="Supplier Cost"
                      value={line.unitCostAtReceipt}
                      onChange={(e) => onReceiveLineChange(index, 'unitCostAtReceipt', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6} md={1}>
                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      inputProps={{ inputMode: 'decimal' }}
                      label="Land"
                      value={line.landingCostAtReceipt}
                      onChange={(e) => onReceiveLineChange(index, 'landingCostAtReceipt', e.target.value)}
                    />
                  </Grid>

                  {receiveMode === 'direct' && (
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        color="error"
                        variant="outlined"
                        onClick={() => removeDirectReceiveLine(index)}
                      >
                        Remove
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          );
        })}

        {receiveMode === 'direct' && (
          <Grid item xs={12}>
            <Button variant="outlined" onClick={addDirectReceiveLine}>Add Receipt Line</Button>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notes"
            value={receiveForm.notes}
            onChange={(e) => onReceiveFieldChange('notes', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={submitReceive}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Posting...' : 'Post Receipt'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
    <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Purchase Order</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedPurchaseOrder
            ? `Cancel PO #${selectedPurchaseOrder.id}. Any outstanding quantity on order will be reversed.`
            : 'Cancel the selected purchase order.'}
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Cancellation Reason"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCancelDialogOpen(false)}>Close</Button>
        <Button color="error" variant="contained" onClick={confirmCancelPurchaseOrder} disabled={isSubmitting}>
          Confirm Cancel
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}